/**
 * 選んだ行への一括の承認 / 却下の操作と、その結末の表示。
 *
 * 選択と送信の状態は画面（`ApprovalsPage`）が持ち、ここは押せるかの判定と結末の描き分け
 * だけを持つ。却下は理由を必須にする — 却下が提案者へ「なぜ通らないか」を返す唯一の経路
 * だからである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選んだ行への一括承認 / 却下の操作と、その結末を描く。
 * @graph-connects none
 */
import { APPROVAL_BATCH_LIMIT, type ApprovalDecision } from '@cortex/domain-spec-console';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { useTranslation } from 'react-i18next';

import type { BatchOutcome } from './batch-outcome.js';

/** `ApprovalActions` の props。 @graph-connects none */
export interface ApprovalActionsProps {
  /** 選んだ行の数（表示中の行のうち）。 */
  selectedCount: number;
  /** どれかの案件で 1 回の上限を超えているか。 */
  overLimit: boolean;
  /** 送信中か。二重送信を防ぐためにボタンを止める。 */
  sending: boolean;
  outcome: BatchOutcome | null;
  reason: string;
  onReasonChange: (reason: string) => void;
  onDecide: (decision: ApprovalDecision, reason: string | null) => void;
}

/**
 * 一括の承認 / 却下の操作。見せるのは「承認する」だけで、却下は「却下する」を開いたときに理由の欄と
 * ボタンを出す（既定は閉じる）。選んだ件数は選んだ箱で数えられるので出さない。却下は理由が空のあいだ押せない。
 *
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選んだ行への一括承認 / 却下の操作と、その結末を描く。
 * @graph-connects none
 */
export function ApprovalActions({
  selectedCount,
  overLimit,
  sending,
  outcome,
  reason,
  onReasonChange,
  onDecide,
}: ApprovalActionsProps) {
  const { t } = useTranslation();
  const trimmedReason = reason.trim();
  const blocked = selectedCount === 0 || sending || overLimit;

  return (
    <div className="flex flex-col gap-3 border-t border-[var(--color-border)] pt-3">
      <div className="flex flex-wrap items-center gap-3" data-selected-count={selectedCount}>
        <Button
          variant="primary"
          size="sm"
          disabled={blocked}
          onClick={() => {
            onDecide('approved', null);
          }}
        >
          {t('approvals.approveSelected', { count: selectedCount })}
        </Button>
      </div>
      {/* 却下は理由を書くときだけ開く。既定は閉じる。 */}
      <details data-approval-reject>
        <summary className="cursor-pointer text-sm text-[var(--color-text-secondary)]">
          {t('approvals.rejectSelected', { count: selectedCount })}
        </summary>
        <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-start">
          <Textarea
            rows={2}
            className="md:max-w-xl"
            value={reason}
            placeholder={t('approvals.reasonPlaceholder')}
            aria-label={t('approvals.reasonPlaceholder')}
            onChange={(event) => {
              onReasonChange(event.target.value);
            }}
          />
          <Button
            variant="danger"
            size="sm"
            className="shrink-0"
            disabled={blocked || trimmedReason === ''}
            onClick={() => {
              onDecide('rejected', trimmedReason);
            }}
          >
            {t('approvals.rejectSelected', { count: selectedCount })}
          </Button>
        </div>
      </details>
      {overLimit && (
        <p className="text-xs text-[var(--color-error)]" role="alert">
          {t('approvals.overLimit', { limit: APPROVAL_BATCH_LIMIT })}
        </p>
      )}
      {outcome?.kind === 'failed' && (
        <p className="text-xs text-[var(--color-error)]" role="alert">
          {t('approvals.failed', { message: outcome.message })}
        </p>
      )}
      {outcome?.kind === 'done' && (
        <div
          className="flex flex-col gap-1 text-xs text-[var(--color-text-secondary)]"
          role="status"
        >
          {outcome.applied > 0 && <span>{t('approvals.applied', { count: outcome.applied })}</span>}
          {outcome.rejections.length > 0 && (
            <span>
              {t('approvals.rejectedByCore', { message: outcome.rejections.join(' / ') })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
